// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { X, AlertTriangle, Activity, ListChecks } from 'lucide-react';
import { getAccountDrilldown } from '../api/workbenchApi';

const fmtUSD = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const STATUS_COLOUR = {
  approved: '#22c55e',
  proposed: '#3b82f6',
  needs_input: '#f59e0b',
  rejected: '#94a3b8',
};

/**
 * Slide-in drawer triggered from any account link. Shows:
 *   - Per-period bar chart (with spike markers).
 *   - Top GL transactions (when available).
 *   - Vendor breakdown.
 *   - Linked workbench adjustments + their analyst status.
 *
 * Data flows through one /qoe/workbench/{id}/account/{name} call which
 * merges results_data + qoe_report + workbench rows server-side.
 */
const AccountDrilldown = ({ mappingSessionId, accountName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!mappingSessionId || !accountName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    getAccountDrilldown(mappingSessionId, accountName)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mappingSessionId, accountName]);

  const chartData = useMemo(() => {
    if (!data?.reported_by_period) return [];
    const spikePeriods = new Set((data.spikes || []).map((s) => s.period));
    return Object.entries(data.reported_by_period).map(([period, value]) => ({
      period,
      value: Number(value || 0),
      spike: spikePeriods.has(period),
    }));
  }, [data]);

  if (!accountName) return null;

  return (
    <div className="qoe-wb-drawer-backdrop" onClick={onClose} role="presentation">
      <aside
        className="qoe-wb-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Drill-down for ${accountName}`}
      >
        <header className="qoe-wb-drawer-header">
          <div>
            <div className="qoe-wb-eyebrow">Account drill-down</div>
            <h2>{accountName}</h2>
            {data?.standardized_category && (
              <div className="qoe-wb-drawer-subtitle">{data.standardized_category}</div>
            )}
          </div>
          <button className="qoe-wb-btn ghost" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <div className="qoe-wb-drawer-body">
          {loading && (
            <div className="qoe-wb-loading">
              <div className="spinner"></div>
            </div>
          )}
          {error && (
            <div className="qoe-wb-row-error fail" style={{ maxWidth: 'unset' }}>
              {error}
            </div>
          )}

          {data && !loading && (
            <>
              {data.flag_reason && (
                <section className="qoe-wb-drawer-section">
                  <h3>
                    <AlertTriangle size={14} /> Flag reasoning
                  </h3>
                  <p className="qoe-wb-drawer-text">{data.flag_reason}</p>
                  {typeof data.flag_confidence === 'number' && (
                    <div className="qoe-wb-drawer-meta">
                      Confidence: {(data.flag_confidence * 100).toFixed(0)}%
                    </div>
                  )}
                </section>
              )}

              <section className="qoe-wb-drawer-section">
                <h3>
                  <Activity size={14} /> Per-period reported values
                </h3>
                {chartData.length === 0 ? (
                  <div className="qoe-wb-drawer-meta">No per-period data.</div>
                ) : (
                  <div style={{ width: '100%', height: 200 }}>
                    <ResponsiveContainer>
                      <BarChart data={chartData} margin={{ top: 8, right: 12, left: 8, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                        <XAxis
                          dataKey="period"
                          tick={{ fontSize: 10, fill: 'var(--text)' }}
                          interval={0}
                          angle={-30}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: 'var(--text)' }}
                          tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                        />
                        <Tooltip
                          formatter={(v) => fmtUSD(v)}
                          contentStyle={{
                            background: 'var(--panel)',
                            border: '1px solid var(--border)',
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        />
                        <Bar dataKey="value">
                          {chartData.map((d, i) => (
                            <Cell key={i} fill={d.spike ? '#f59e0b' : 'var(--accent)'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {data.spikes && data.spikes.length > 0 && (
                  <div className="qoe-wb-drawer-meta">
                    Spikes (z &gt; 2): {data.spikes.map((s) => `${s.period} (${s.z_score.toFixed(1)}σ)`).join(', ')}
                  </div>
                )}
              </section>

              {data.adjustments && data.adjustments.length > 0 && (
                <section className="qoe-wb-drawer-section">
                  <h3>
                    <ListChecks size={14} /> Linked adjustments ({data.adjustments.length})
                  </h3>
                  <div className="qoe-wb-drawer-adj-list">
                    {data.adjustments.map((a) => (
                      <div key={a.id} className="qoe-wb-drawer-adj">
                        <div className="qoe-wb-drawer-adj-head">
                          <span
                            className="qoe-wb-drawer-adj-status"
                            style={{ background: STATUS_COLOUR[a.status] || '#94a3b8' }}
                          />
                          <span className="qoe-wb-drawer-adj-cat">{a.category.replace(/_/g, ' ')}</span>
                          <span className="qoe-wb-drawer-adj-status-label">{a.status.replace(/_/g, ' ')}</span>
                          <span className="qoe-wb-drawer-adj-impact">
                            {a.status === 'approved' ? fmtUSD(a.annual_impact) : '—'}
                          </span>
                        </div>
                        {a.llm_reasoning && (
                          <div className="qoe-wb-drawer-adj-reason">{a.llm_reasoning}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {data.top_transactions && data.top_transactions.length > 0 && (
                <section className="qoe-wb-drawer-section">
                  <h3>Top transactions ({data.transaction_count} total)</h3>
                  <div className="qoe-wb-drawer-table-wrap">
                    <table className="qoe-wb-drawer-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Vendor</th>
                          <th>Description</th>
                          <th className="amount">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_transactions.map((t, i) => (
                          <tr key={i}>
                            <td>{(t.date || '').toString().split(' ')[0]}</td>
                            <td>{t.vendor || '—'}</td>
                            <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.description}>
                              {t.description || '—'}
                            </td>
                            <td className="amount" style={{ color: t.is_debit ? undefined : '#ef4444' }}>
                              {fmtUSD(t.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {data.vendor_breakdown && data.vendor_breakdown.length > 0 && (
                <section className="qoe-wb-drawer-section">
                  <h3>Top vendors</h3>
                  <div className="qoe-wb-drawer-table-wrap">
                    <table className="qoe-wb-drawer-table">
                      <thead>
                        <tr>
                          <th>Vendor</th>
                          <th className="amount">Txns</th>
                          <th className="amount">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.vendor_breakdown.map((v, i) => (
                          <tr key={i}>
                            <td>{v.vendor}</td>
                            <td className="amount">{v.txn_count}</td>
                            <td className="amount">{fmtUSD(v.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default AccountDrilldown;
